import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'jalali-moment';

import './JalaliDatePicker.style.scss';

const toJalaliParts = dateStr => {
	if (!dateStr)
		return null;
	const parsed = moment(String(dateStr).substring(0, 10), 'YYYY-MM-DD', true);
	if (!parsed.isValid())
		return null;
	return {
		year: parsed.jYear(),
		month: parsed.jMonth() + 1,
		day: parsed.jDate()
	};
};

const toGregorian = (year, month, day) => {
	if (!year || !month || !day)
		return '';
	const parsed = moment.from(`${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`, 'fa', 'jYYYY/jMM/jDD');
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const toShamsi = (year, month, day) => {
	if (!year || !month || !day)
		return '';
	return `${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
};

const jalaliDaysInMonth = (year, month) => {
	if (!month)
		return 31;
	if (month <= 6)
		return 31;
	if (month <= 11)
		return 30;
	if (!year)
		return 30;
	return moment.jIsLeapYear(Number(year)) ? 30 : 29;
};

const JalaliDatePicker = ({
	fieldKey,
	title,
	value = null,
	minDate = null,
	maxDate = null,
	yearsBack = 10,
	yearsForward = 0,
	disabled = false,
	required = false,
	isSearch = true,
	onChange = null
}) => {
	const { t } = useTranslation();
	const currentJalaliYear = moment().jYear();
	const minParts = useMemo(() => toJalaliParts(minDate), [minDate]);
	const maxParts = useMemo(() => toJalaliParts(maxDate), [maxDate]);
	const valueParts = useMemo(() => toJalaliParts(value), [value]);

	const years = useMemo(() => {
		const maxYear = currentJalaliYear + yearsForward;
		const minYear = currentJalaliYear - yearsBack;
		const extraYear = valueParts?.year;
		const startYear = extraYear && extraYear < minYear ? extraYear : minYear;
		const endYear = extraYear && extraYear > maxYear ? extraYear : maxYear;
		const list = [];
		for (let year = endYear; year >= startYear; year--)
			list.push(year);
		return list;
	}, [currentJalaliYear, yearsBack, yearsForward, valueParts]);

	const [year, setYear] = useState('');
	const [month, setMonth] = useState('');
	const [day, setDay] = useState('');
	const lastEmittedValue = useRef(undefined);

	useEffect(() => {
		// A partial edit emits null; its parent echo must not clear the remaining selections.
		const emittedValue = lastEmittedValue.current;
		lastEmittedValue.current = undefined;
		if(emittedValue === value) {
			return;
		}
		const parts = toJalaliParts(value);
		if (parts) {
			setYear(String(parts.year));
			setMonth(String(parts.month));
			setDay(String(parts.day));
			return;
		}
		if (!value) {
			setYear('');
			setMonth('');
			setDay('');
		}
	}, [value]);

	useEffect(() => {
		const handleFormReset = () => {
			lastEmittedValue.current = undefined;
			setYear('');
			setMonth('');
			setDay('');
		};
		window.addEventListener('form-reset', handleFormReset);
		return () => window.removeEventListener('form-reset', handleFormReset);
	}, []);

	const daysInMonth = jalaliDaysInMonth(year, Number(month));

	const isYearDisabled = optionYear => {
		if (minParts && optionYear < minParts.year)
			return true;
		if (maxParts && optionYear > maxParts.year)
			return true;
		return false;
	};

	const isMonthDisabled = optionMonth => {
		if (!year)
			return false;
		const selectedYear = Number(year);
		if (minParts && selectedYear === minParts.year && optionMonth < minParts.month)
			return true;
		if (maxParts && selectedYear === maxParts.year && optionMonth > maxParts.month)
			return true;
		return false;
	};

	const isDayDisabled = optionDay => {
		if (!year || !month)
			return false;
		const selectedYear = Number(year);
		const selectedMonth = Number(month);
		if (minParts && selectedYear === minParts.year && selectedMonth === minParts.month && optionDay < minParts.day)
			return true;
		if (maxParts && selectedYear === maxParts.year && selectedMonth === maxParts.month && optionDay > maxParts.day)
			return true;
		return false;
	};

	const applyChange = (nextYear, nextMonth, nextDay) => {
		const maxDays = jalaliDaysInMonth(nextYear, Number(nextMonth));
		const nextDayValue = nextDay && Number(nextDay) > maxDays ? '' : nextDay;
		setYear(nextYear);
		setMonth(nextMonth);
		setDay(nextDayValue);
		const nextValue = toGregorian(nextYear, nextMonth, nextDayValue) || null;
		if(onChange) {
			lastEmittedValue.current = nextValue;
			onChange(nextValue);
		}
	};

	useEffect(() => {
		if (year && isYearDisabled(Number(year))) {
			applyChange('', '', '');
			return;
		}
		if (month && isMonthDisabled(Number(month))) {
			applyChange(year, '', '');
			return;
		}
		if (day && isDayDisabled(Number(day)))
			applyChange(year, month, '');
	}, [minDate, maxDate]);

	const hiddenValue = toShamsi(year, month, day);

	return (
		<div className={(isSearch ? 'col-sm-6 col-md-4' : 'col-md-6') + ' mb-3'}>
			<label className="form-label" htmlFor={`${fieldKey}-year`}>
				{title}
				{required ? <span className="text-danger"> *</span> : ''}
			</label>
			<div className="jalali-datepicker">
				<div className="jalali-datepicker__box form-control" dir="ltr">
					<select
						className={`jalali-datepicker__select jalali-datepicker__select--year${year ? ' is-filled' : ''}`}
						id={`${fieldKey}-year`}
						aria-label={`${title} - ${t('year')}`}
						value={year}
						disabled={disabled}
						onChange={event => applyChange(event.target.value, month, day)}
					>
						<option value="">{t('year')}</option>
						{years.map(optionYear =>
							<option key={optionYear} value={optionYear} disabled={isYearDisabled(optionYear)}>
								{optionYear}
							</option>
						)}
					</select>
					<span className="jalali-datepicker__divider" aria-hidden="true"></span>
					<select
						className={`jalali-datepicker__select jalali-datepicker__select--month${month ? ' is-filled' : ''}`}
						id={`${fieldKey}-month`}
						aria-label={`${title} - ${t('month')}`}
						value={month}
						disabled={disabled}
						onChange={event => applyChange(year, event.target.value, day)}
					>
						<option value="">{t('month')}</option>
						{Array.from({ length: 12 }, (_, index) => index + 1).map(optionMonth =>
							<option key={optionMonth} value={optionMonth} disabled={isMonthDisabled(optionMonth)}>
								{t(`jalali_months.${optionMonth}`)}
							</option>
						)}
					</select>
					<span className="jalali-datepicker__divider" aria-hidden="true"></span>
					<select
						className={`jalali-datepicker__select jalali-datepicker__select--day${day ? ' is-filled' : ''}`}
						id={`${fieldKey}-day`}
						aria-label={`${title} - ${t('day')}`}
						value={day}
						disabled={disabled}
						onChange={event => applyChange(year, month, event.target.value)}
					>
						<option value="">{t('day')}</option>
						{Array.from({ length: daysInMonth }, (_, index) => index + 1).map(optionDay =>
							<option key={optionDay} value={optionDay} disabled={isDayDisabled(optionDay)}>
								{optionDay}
							</option>
						)}
					</select>
				</div>
			</div>
			<input type="hidden" name={fieldKey} value={hiddenValue} data-incomplete={!!(year || month || day) && !hiddenValue} />
		</div>
	);
};

export default JalaliDatePicker;
